import { memo, useCallback } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { type BoardCustomFieldValue } from '@air/api/types';

import {
  CustomFieldPlainText,
  type CustomFieldPlainTextProps,
} from '~/components/CustomFields/CustomFieldInput/CustomFieldPlainText';
import { useUpdatePublicItemsCustomFieldValue } from '~/utils/CustomFields/useUpdatePublicItemsCustomFieldValue';

export type PublicBoardCustomFieldPlainTextProps = {
  field: BoardCustomFieldValue;
  trackingLocation: AirActionTrackingLocation;
  canEditCustomFields: boolean;
  boardId: string;
} & Omit<CustomFieldPlainTextProps, 'onSubmit'>;

export const PublicBoardCustomFieldPlainText = memo(
  ({ field, trackingLocation, canEditCustomFields, boardId, ...rest }: PublicBoardCustomFieldPlainTextProps) => {
    const { updatePublicItemsCustomFieldValue } = useUpdatePublicItemsCustomFieldValue();

    const updateCustomFieldValue = useCallback(
      async (value: string) => {
        await updatePublicItemsCustomFieldValue({
          boardIds: [boardId],
          baseCustomField: field,
          nextPlainTextValue: value,
          trackingLocation,
        });
      },
      [boardId, updatePublicItemsCustomFieldValue, field, trackingLocation],
    );

    return (
      <CustomFieldPlainText
        {...rest}
        onSubmit={canEditCustomFields ? updateCustomFieldValue : undefined}
        field={field}
      />
    );
  },
);

PublicBoardCustomFieldPlainText.displayName = 'PublicBoardCustomFieldPlainText';
